import React, { Component } from 'react';

// ── helpers ───────────────────────────────────────────────────────────────────

function domainOf(email) {
  const p = (email || '').split('@');
  return p.length === 2 ? p[1].toLowerCase() : '';
}

function groupByDomain(rows) {
  const map = {};
  rows.forEach(r => {
    const d = domainOf(r.email || r.Email || '');
    if (!d) return;
    if (!map[d]) map[d] = [];
    map[d].push(r);
  });
  return map;
}

function relTime(ts) {
  if (!ts) return '—';
  const diff = Date.now() - new Date(ts).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const CARD = { backgroundColor: 'rgba(33,37,41,0.6)', backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,0.1)' };
const HEADER_STYLE = { backgroundColor: 'rgba(33,37,41,0.8)', backdropFilter: 'blur(4px)', borderBottom: '1px solid rgba(255,255,255,0.1)', padding: '12px 20px' };

// ── tier badge ────────────────────────────────────────────────────────────────
function TierBadge({ tier }) {
  const map = { free: 'secondary', pro: 'info', enterprise: 'warning' };
  return <span className={`badge bg-${map[tier] || 'secondary'} ms-1`}>{tier || '—'}</span>;
}

// ── limit display ─────────────────────────────────────────────────────────────
function Limit({ n }) {
  return <span className="text-secondary">{n === -1 ? '∞' : n}</span>;
}

// ── main component ────────────────────────────────────────────────────────────
export default class Admin extends Component {
  constructor(props) {
    super(props);
    this.state = {
      token: sessionStorage.getItem('admin_token') || '',
      password: '',
      loginError: null,
      loginLoading: false,

      tab: 'licenses',

      licenses: [],
      beacons: [],
      contacts: [],
      signups: [],
      payments: [],
      connections: [],

      loading: false,
      error: null,

      lastRefreshed: null,

      // new-license form
      newOrg: '',
      newTier: 'enterprise',
      newDays: 30,
      genResult: null,
      genLoading: false,

      // inline edit
      editKey: null,
      editFields: {},
      editLoading: false,
      editError: null,
    };
    this.refreshTimer = null;
  }

  componentDidMount() {
    if (this.state.token) {
      document.title = 'ForeignSix | Admin Portal';
      this.fetchAll();
      this.startAutoRefresh();
    }
  }

  componentWillUnmount() {
    document.title = 'ForeignSix | Incident Response Orchestration';
    this.stopAutoRefresh();
  }

  startAutoRefresh = () => {
    this.stopAutoRefresh();
    this.refreshTimer = setInterval(this.fetchAll, 30000);
  };

  stopAutoRefresh = () => {
    if (this.refreshTimer) { clearInterval(this.refreshTimer); this.refreshTimer = null; }
  };

  // ── auth ────────────────────────────────────────────────────────────────────

  handleLogin = async (e) => {
    e.preventDefault();
    this.setState({ loginLoading: true, loginError: null });
    try {
      const res = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: this.state.password }),
      });
      if (res.status === 401) { this.setState({ loginError: 'Incorrect password.', loginLoading: false }); return; }
      if (!res.ok) throw new Error();
      const { token } = await res.json();
      sessionStorage.setItem('admin_token', token);
      document.title = 'ForeignSix | Admin Portal';
      this.setState({ token, loginLoading: false, password: '' }, () => { this.fetchAll(); this.startAutoRefresh(); });
    } catch {
      this.setState({ loginError: 'Login failed, try again.', loginLoading: false });
    }
  };

  handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    document.title = 'ForeignSix | Incident Response Orchestration';
    this.stopAutoRefresh();
    this.setState({ token: '', licenses: [], beacons: [], contacts: [], signups: [], payments: [], lastRefreshed: null });
  };

  // ── data fetching ───────────────────────────────────────────────────────────

  authHeaders = () => ({ 'Authorization': `Bearer ${this.state.token}`, 'Content-Type': 'application/json' });

  fetchAll = () => {
    this.fetchTab('licenses');
    this.fetchTab('beacons');
    this.fetchTab('contacts');
    this.fetchTab('signups');
    this.fetchTab('payments');
    this.fetchTab('connections');
    this.setState({ lastRefreshed: new Date() });
  };

  fetchTab = async (tab) => {
    try {
      const res = await fetch(`/api/admin/${tab}`, { headers: this.authHeaders() });
      if (res.status === 401) { this.handleLogout(); return; }
      if (!res.ok) return;
      const data = await res.json();
      this.setState({ [tab]: data || [] });
    } catch { /* silent */ }
  };

  // ── license actions ─────────────────────────────────────────────────────────

  handleGenerate = async (e) => {
    e.preventDefault();
    this.setState({ genLoading: true, genResult: null });
    try {
      const res = await fetch('/api/admin/licenses', {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ organization: this.state.newOrg, tier: this.state.newTier, days: Number(this.state.newDays) }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      this.setState({ genResult: data, genLoading: false, newOrg: '' });
      this.fetchTab('licenses');
    } catch {
      this.setState({ genLoading: false, genResult: { error: 'Failed to generate license.' } });
    }
  };

  handleRevoke = async (key) => {
    if (!window.confirm(`Revoke license key ${key}? The license will be expired immediately but kept on record.`)) return;
    try {
      const res = await fetch(`/api/admin/licenses?key=${encodeURIComponent(key)}`, { method: 'DELETE', headers: this.authHeaders() });
      if (res.ok) this.fetchTab('licenses');
    } catch { /* silent */ }
  };

  handleEditOpen = (l) => {
    const d = new Date(l.expires_at);
    const pad = n => String(n).padStart(2, '0');
    const expiresDate = `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
    this.setState({
      editKey: l.server_license,
      editFields: { expires_at: expiresDate, max_agents: l.max_agents, max_acquisitions: l.max_acquisitions, max_analysts: l.max_analysts, max_memory_acquisitions: l.max_memory_acquisitions },
      editError: null,
    });
  };

  handleEditChange = (field, value) => {
    this.setState(prev => ({ editFields: { ...prev.editFields, [field]: value } }));
  };

  handleEditSave = async () => {
    const { editKey, editFields } = this.state;
    this.setState({ editLoading: true, editError: null });
    try {
      const body = {
        key: editKey,
        expires_at: editFields.expires_at ? new Date(editFields.expires_at + 'T12:00:00Z').toISOString() : undefined,
        max_agents: Number(editFields.max_agents),
        max_acquisitions: Number(editFields.max_acquisitions),
        max_analysts: Number(editFields.max_analysts),
        max_memory_acquisitions: Number(editFields.max_memory_acquisitions),
      };
      const res = await fetch('/api/admin/licenses', { method: 'PATCH', headers: this.authHeaders(), body: JSON.stringify(body) });
      if (!res.ok) throw new Error(await res.text());
      this.setState({ editKey: null, editFields: {}, editLoading: false });
      this.fetchTab('licenses');
    } catch (err) {
      this.setState({ editLoading: false, editError: err.message || 'Save failed.' });
    }
  };

  handleEditCancel = () => this.setState({ editKey: null, editFields: {}, editError: null });

  // ── render ──────────────────────────────────────────────────────────────────

  render() {
    const { token, password, loginError, loginLoading, tab,
            licenses, beacons, contacts, signups, payments, connections,
            lastRefreshed,
            newOrg, newTier, newDays, genResult, genLoading,
            editKey, editFields, editLoading, editError } = this.state;

    if (!token) {
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="card text-light p-4" style={{ ...CARD, width: '100%', maxWidth: 360 }}>
            <h5 className="mb-4 text-center">Admin Login</h5>
            <form onSubmit={this.handleLogin}>
              <div className="mb-3">
                <input
                  type="password"
                  className="form-control bg-dark text-light border-secondary"
                  placeholder="Admin password"
                  value={password}
                  onChange={e => this.setState({ password: e.target.value })}
                  autoFocus
                />
              </div>
              {loginError && <p className="text-danger small mb-2">{loginError}</p>}
              <button className="btn btn-info w-100" type="submit" disabled={loginLoading}>
                {loginLoading ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
          </div>
        </div>
      );
    }

    const tabs = [
      { key: 'licenses', label: 'Licenses' },
      { key: 'beacons',  label: 'Component Beacons'  },
      { key: 'signups',  label: 'Free Trial Signups'  },
      { key: 'payments', label: 'Pro Payments' },
      { key: 'contacts', label: 'Enterprise Enquiries' },
      { key: 'orgs',     label: 'Orgs'     },
      { key: 'connections', label: 'Connections' },
    ];

    return (
      <div>
        {/* Admin header */}
        <div className="d-flex justify-content-between align-items-center mb-3" style={HEADER_STYLE}>
          <span className="text-light fw-semibold">ForeignSix Admin Portal</span>
          <div className="d-flex align-items-center gap-2">
            {lastRefreshed && (
              <small className="text-secondary">
                Updated {lastRefreshed.toLocaleTimeString()} · auto-refresh every 30s
              </small>
            )}
            <button className="btn btn-outline-secondary btn-sm" onClick={this.fetchAll}>Refresh</button>
            <button className="btn btn-outline-danger btn-sm" onClick={this.handleLogout}>Logout</button>
          </div>
        </div>

        {/* Tabs + content */}
        <div style={{ padding: '0 20px 20px' }}>
          <ul className="nav nav-tabs mb-3">
            {tabs.map(t => (
              <li className="nav-item" key={t.key}>
                <button
                  className={`nav-link${tab === t.key ? ' active' : ''}`}
                  onClick={() => this.setState({ tab: t.key }, () => t.key !== 'orgs' && this.fetchTab(t.key))}
                >{t.label}</button>
              </li>
            ))}
          </ul>

          {tab === 'licenses'  && this.renderLicenses(licenses, newOrg, newTier, newDays, genResult, genLoading, editKey, editFields, editLoading, editError)}
          {tab === 'beacons'   && this.renderBeacons(beacons)}
          {tab === 'signups'   && this.renderSignups(signups)}
          {tab === 'payments'  && this.renderPayments(payments)}
          {tab === 'contacts'  && this.renderContacts(contacts)}
          {tab === 'orgs'      && this.renderOrgs(signups, contacts, payments)}
          {tab === 'connections' && this.renderConnections(connections)}
        </div>
      </div>
    );
  }

  // ── licenses tab ─────────────────────────────────────────────────────────────
  renderLicenses(licenses, newOrg, newTier, newDays, genResult, genLoading, editKey, editFields, editLoading, editError) {
    return (
      <div>
        {/* Generate form */}
        <div className="card text-light mb-4 p-3" style={CARD}>
          <h6 className="mb-3">Generate License</h6>
          <form onSubmit={this.handleGenerate} className="row g-2 align-items-end">
            <div className="col-sm-4">
              <input required className="form-control form-control-sm bg-dark text-light border-secondary"
                placeholder="Organisation name" value={newOrg}
                onChange={e => this.setState({ newOrg: e.target.value })} />
            </div>
            <div className="col-sm-2">
              <select className="form-select form-select-sm bg-dark text-light border-secondary"
                value={newTier} onChange={e => this.setState({ newTier: e.target.value })}>
                <option value="free">free</option>
                <option value="pro">pro</option>
                <option value="enterprise">enterprise</option>
              </select>
            </div>
            <div className="col-sm-2">
              <input type="number" min="1" className="form-control form-control-sm bg-dark text-light border-secondary"
                placeholder="Days" value={newDays}
                onChange={e => this.setState({ newDays: e.target.value })} />
            </div>
            <div className="col-sm-2">
              <button className="btn btn-info btn-sm w-100" type="submit" disabled={genLoading}>
                {genLoading ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </form>
          {genResult && !genResult.error && (
            <div className="mt-3 small font-monospace">
              <div><span className="text-secondary">Server: </span><span className="text-warning">{genResult.server_license}</span></div>
              <div><span className="text-secondary">Agent:  </span><span className="text-warning">{genResult.agent_license}</span></div>
              <div><span className="text-secondary">Tier:   </span><TierBadge tier={genResult.tier} /></div>
              <div><span className="text-secondary">Expires: </span>{genResult.expires_at ? new Date(genResult.expires_at).toLocaleDateString() : '—'}</div>
            </div>
          )}
          {genResult?.error && <p className="text-danger small mt-2 mb-0">{genResult.error}</p>}
        </div>

        {/* License table */}
        <div className="card text-light p-3" style={CARD}>
          <h6 className="mb-3">Active Licenses <span className="text-secondary fw-normal small ms-1">({licenses.length})</span></h6>
          <div className="table-responsive">
            <table className="table table-dark table-sm table-hover small mb-0">
              <thead>
                <tr>
                  <th>Organisation</th>
                  <th>Tier</th>
                  <th>Agents</th>
                  <th>Acq.</th>
                  <th>RAM</th>
                  <th>Analysts</th>
                  <th>Server License</th>
                  <th>Agent License</th>
                  <th>Expires</th>
                  <th>Days left</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {licenses.length === 0 && <tr><td colSpan={11} className="text-secondary text-center">No licenses</td></tr>}
                {licenses.map((l, i) => (
                  <React.Fragment key={i}>
                    <tr className={l.revoked ? 'opacity-50' : ''}>
                      <td>{l.organization}{l.revoked && <span className="badge bg-danger ms-1" style={{ fontSize: '0.65rem' }}>Revoked</span>}</td>
                      <td><TierBadge tier={l.tier} /></td>
                      <td><Limit n={l.max_agents} /></td>
                      <td><Limit n={l.max_acquisitions} /></td>
                      <td><Limit n={l.max_memory_acquisitions} /></td>
                      <td><Limit n={l.max_analysts} /></td>
                      <td className="font-monospace text-warning" style={{ fontSize: '0.75rem' }}>{l.server_license}</td>
                      <td className="font-monospace text-warning" style={{ fontSize: '0.75rem' }}>{l.agent_license}</td>
                      <td>{l.expires_at ? new Date(l.expires_at).toLocaleDateString() : '—'}</td>
                      <td className={l.days_remaining < 7 ? 'text-danger' : 'text-secondary'}>{l.days_remaining}d</td>
                      <td className="d-flex gap-1">
                        <button className="btn btn-outline-secondary btn-sm py-0 px-1"
                          onClick={() => editKey === l.server_license ? this.handleEditCancel() : this.handleEditOpen(l)}>
                          {editKey === l.server_license ? 'Cancel' : 'Edit'}
                        </button>
                        {!l.revoked && (
                          <button className="btn btn-outline-danger btn-sm py-0 px-1"
                            onClick={() => this.handleRevoke(l.server_license)}>Revoke</button>
                        )}
                      </td>
                    </tr>
                    {editKey === l.server_license && (
                      <tr>
                        <td colSpan={11} className="p-2" style={{ backgroundColor: 'rgba(0,0,0,0.3)', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                          <div className="d-flex gap-3 align-items-end flex-wrap">
                            <div>
                              <div className="text-secondary small mb-1">Expiry date</div>
                              <input type="date" className="form-control form-control-sm bg-dark text-light border-secondary"
                                value={editFields.expires_at || ''}
                                onChange={e => this.handleEditChange('expires_at', e.target.value)} />
                            </div>
                            <div>
                              <div className="text-secondary small mb-1">Max Agents <span className="text-secondary" style={{ fontSize: '0.7rem' }}>(-1 = ∞)</span></div>
                              <input type="number" className="form-control form-control-sm bg-dark text-light border-secondary" style={{ width: 90 }}
                                value={editFields.max_agents ?? ''} onChange={e => this.handleEditChange('max_agents', e.target.value)} />
                            </div>
                            <div>
                              <div className="text-secondary small mb-1">Max Acquisitions</div>
                              <input type="number" className="form-control form-control-sm bg-dark text-light border-secondary" style={{ width: 90 }}
                                value={editFields.max_acquisitions ?? ''} onChange={e => this.handleEditChange('max_acquisitions', e.target.value)} />
                            </div>
                            <div>
                              <div className="text-secondary small mb-1">Max Memory Captures</div>
                              <input type="number" className="form-control form-control-sm bg-dark text-light border-secondary" style={{ width: 90 }}
                                value={editFields.max_memory_acquisitions ?? ''} onChange={e => this.handleEditChange('max_memory_acquisitions', e.target.value)} />
                            </div>
                            <div>
                              <div className="text-secondary small mb-1">Max Analysts</div>
                              <input type="number" className="form-control form-control-sm bg-dark text-light border-secondary" style={{ width: 90 }}
                                value={editFields.max_analysts ?? ''} onChange={e => this.handleEditChange('max_analysts', e.target.value)} />
                            </div>
                            <button className="btn btn-success btn-sm" onClick={this.handleEditSave} disabled={editLoading}>
                              {editLoading ? 'Saving…' : 'Save'}
                            </button>
                          </div>
                          {editError && <p className="text-danger small mt-2 mb-0">{editError}</p>}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // ── beacons tab ───────────────────────────────────────────────────────────────
  renderBeacons(beacons) {
    return (
      <div className="card text-light p-3" style={CARD}>
        <h6 className="mb-3">Recent Beacons <span className="text-secondary fw-normal small ms-1">({beacons.length})</span></h6>
        <div className="table-responsive">
          <table className="table table-dark table-sm table-hover small mb-0">
            <thead>
              <tr><th>When</th><th>Type</th><th>Hostname</th><th>OS</th><th>Arch</th><th>CPUs</th><th>Version</th><th>IP</th><th>Status</th><th>Port</th></tr>
            </thead>
            <tbody>
              {beacons.length === 0 && <tr><td colSpan={10} className="text-secondary text-center">No beacons yet</td></tr>}
              {beacons.map((b, i) => (
                <tr key={i}>
                  <td className="text-secondary">{relTime(b.received_at)}</td>
                  <td><span className={`badge ${b.type === 'server' ? 'bg-primary' : 'bg-success'}`}>{b.type}</span></td>
                  <td className="font-monospace">{b.hostname}</td>
                  <td>{b.os}</td>
                  <td>{b.arch}</td>
                  <td>{b.cpus}</td>
                  <td className="text-secondary">{b.version}</td>
                  <td className="font-monospace text-secondary" style={{ fontSize: '0.75rem' }}>{b.ip}</td>
                  <td>{b.status}{b.reason ? <span className="text-secondary ms-1">({b.reason})</span> : null}</td>
                  <td>{b.port || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ── signups tab ───────────────────────────────────────────────────────────────
  renderSignups(signups) {
    return (
      <div className="card text-light p-3" style={CARD}>
        <h6 className="mb-3">Access Requests <span className="text-secondary fw-normal small ms-1">({signups.length})</span></h6>
        <div className="table-responsive">
          <table className="table table-dark table-sm table-hover small mb-0">
            <thead>
              <tr><th>When</th><th>Name</th><th>Email</th><th>Organisation</th><th>Tier</th><th>Billing</th><th>Verified</th><th>Email status</th></tr>
            </thead>
            <tbody>
              {signups.length === 0 && <tr><td colSpan={8} className="text-secondary text-center">No signups yet</td></tr>}
              {signups.map((s, i) => (
                <tr key={i}>
                  <td className="text-secondary">{relTime(s.created_at)}</td>
                  <td>{s.name}</td>
                  <td className="font-monospace" style={{ fontSize: '0.8rem' }}>{s.email}</td>
                  <td>{s.organization}</td>
                  <td><TierBadge tier={s.tier} /></td>
                  <td className="text-secondary">{s.billing || '—'}</td>
                  <td>{s.verified
                    ? <span className="text-success">✓ Yes</span>
                    : <span className="text-secondary">Pending</span>}
                  </td>
                  <td>
                    {s.email_error
                      ? <span className="text-danger" title={s.email_error}>⚠ Failed: <a href={s.verify_url} className="text-warning font-monospace" style={{ fontSize: '0.75rem', wordBreak: 'break-all' }} target="_blank" rel="noreferrer">copy link</a></span>
                      : s.verify_url
                        ? <span className="text-success">Sent</span>
                        : <span className="text-secondary">—</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ── payments tab ──────────────────────────────────────────────────────────────
  renderPayments(payments) {
    return (
      <div className="card text-light p-3" style={CARD}>
        <h6 className="mb-3">Stripe Payments <span className="text-secondary fw-normal small ms-1">({payments.length})</span></h6>
        <div className="table-responsive">
          <table className="table table-dark table-sm table-hover small mb-0">
            <thead>
              <tr><th>When</th><th>Organisation</th><th>Email</th><th>Billing</th><th>Session ID</th></tr>
            </thead>
            <tbody>
              {payments.length === 0 && <tr><td colSpan={5} className="text-secondary text-center">No payments yet</td></tr>}
              {payments.map((p, i) => (
                <tr key={i}>
                  <td className="text-secondary">{relTime(p.received_at)}</td>
                  <td>{p.organization}</td>
                  <td className="font-monospace" style={{ fontSize: '0.8rem' }}>{p.email}</td>
                  <td><span className="badge bg-success">{p.billing}</span></td>
                  <td className="font-monospace text-secondary" style={{ fontSize: '0.75rem' }}>{p.session_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ── contacts tab ──────────────────────────────────────────────────────────────
  renderContacts(contacts) {
    return (
      <div className="card text-light p-3" style={CARD}>
        <h6 className="mb-3">Enterprise Enquiries <span className="text-secondary fw-normal small ms-1">({contacts.length})</span></h6>
        <div className="table-responsive">
          <table className="table table-dark table-sm table-hover small mb-0">
            <thead>
              <tr><th>When</th><th>Name</th><th>Email</th><th>Organisation</th><th>Message</th></tr>
            </thead>
            <tbody>
              {contacts.length === 0 && <tr><td colSpan={5} className="text-secondary text-center">No enquiries yet</td></tr>}
              {contacts.map((c, i) => (
                <tr key={i}>
                  <td className="text-secondary">{relTime(c.submitted_at)}</td>
                  <td>{c.name}</td>
                  <td className="font-monospace" style={{ fontSize: '0.8rem' }}>{c.email}</td>
                  <td>{c.organization}</td>
                  <td style={{ maxWidth: 300, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{c.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ── connections tab ──────────────────────────────────────────────────────────
  renderConnections(connections) {
    if (!connections) return <div className="card text-light p-3" style={CARD}><p className="text-secondary mb-0 text-center">Loading connection data…</p></div>;
    if (connections.length === 0) return <div className="card text-light p-3" style={CARD}><p className="text-secondary mb-0 text-center">No tenants have connected yet.</p></div>;
    return (
      <div className="card text-light p-3" style={CARD}>
        <table className="table table-dark table-sm small mb-0">
          <thead>
            <tr><th>Organisation</th><th>Org Key</th><th>Active Agents</th><th>Max Agents</th></tr>
          </thead>
          <tbody>
            {connections.map(c => (
              <tr key={c.org_key}>
                <td>{c.organization}</td>
                <td className="font-monospace text-secondary" style={{ fontSize: '0.8rem' }}>{c.org_key}</td>
                <td className="font-monospace">{c.active_agents}</td>
                <td className="font-monospace"><Limit n={c.max_agents} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-secondary small mt-2 mb-0">
          Active agent counts are derived from the most recent beacon each tenant's IR server has sent; per-device
          detail (hostname, IP, version) is visible in that tenant's own IR server dashboard, not here.
        </p>
      </div>
    );
  }

  // ── orgs tab ──────────────────────────────────────────────────────────────────
  renderOrgs(signups, contacts, payments) {
    // Merge all email-bearing records and group by TLD/domain
    const all = [
      ...signups.map(r => ({ email: r.email, name: r.name, org: r.organization, source: 'signup', tier: r.tier, when: r.created_at })),
      ...contacts.map(r => ({ email: r.email, name: r.name, org: r.organization, source: 'contact', when: r.submitted_at })),
      ...payments.map(r => ({ email: r.email, name: '', org: r.organization, source: 'payment', when: r.received_at })),
    ];

    const grouped = groupByDomain(all);
    const domains = Object.keys(grouped).sort((a, b) => grouped[b].length - grouped[a].length);

    return (
      <div>
        {domains.length === 0 && (
          <div className="card text-light p-3" style={CARD}>
            <p className="text-secondary mb-0 text-center">No data yet: signups, contacts and payments will appear here grouped by email domain.</p>
          </div>
        )}
        {domains.map(domain => {
          const rows = grouped[domain];
          const orgNames = [...new Set(rows.map(r => r.org).filter(Boolean))].join(', ');
          return (
            <div key={domain} className="card text-light p-3 mb-3" style={CARD}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0 font-monospace">@{domain}</h6>
                <div className="d-flex gap-2">
                  {orgNames && <span className="text-secondary small">{orgNames}</span>}
                  <span className="badge bg-secondary">{rows.length} contact{rows.length !== 1 ? 's' : ''}</span>
                </div>
              </div>
              <table className="table table-dark table-sm small mb-0">
                <thead>
                  <tr><th>Email</th><th>Name</th><th>Source</th><th>Tier</th><th>When</th></tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i}>
                      <td className="font-monospace" style={{ fontSize: '0.8rem' }}>{r.email}</td>
                      <td>{r.name || '—'}</td>
                      <td><span className={`badge ${r.source === 'payment' ? 'bg-success' : r.source === 'contact' ? 'bg-warning text-dark' : 'bg-secondary'}`}>{r.source}</span></td>
                      <td>{r.tier ? <TierBadge tier={r.tier} /> : '—'}</td>
                      <td className="text-secondary">{relTime(r.when)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    );
  }
}
